import { useAuthFetch } from "@p0security/frontend-core";
import { FirestoreDoc } from "@p0security/frontend-core/react";
import { DEFAULT_DURATION_SETTINGS } from "@p0security/shared/configuration/constant";
import { DurationOption } from "@p0security/shared/configuration/types";
import { toRequestAttribution } from "@p0security/shared/permission-requests/attribution";
import {
  convertToDurationOption,
  sortApprovalOptions,
} from "@p0security/shared/permission-requests/util";
import { PermissionRequest } from "@p0security/shared/types/permission";
import { Button, Modal, Select, Typography } from "@p0security/ui";
import { useDurationSettings } from "components/Admin/useDurationSettings";
import { Identity } from "components/common/Identity";
import { compact, uniqBy } from "lodash";
import { useCallback, useMemo, useState } from "react";

import {
  handleRequestActionResponse,
  requestDescription,
} from "../requestUtils";

const buildCustomRequestDurationOption = (
  duration: string | undefined,
  originalRequestedDuration?: string
): DurationOption | undefined => {
  const customDuration = duration
    ? convertToDurationOption(duration)
    : undefined;
  if (!customDuration) return undefined;

  return {
    ...customDuration,
    value: originalRequestedDuration
      ? `${customDuration.value} (max allowable - requested ${convertToDurationOption(originalRequestedDuration).value})`
      : `${customDuration.value} (requested)`,
  };
};

export const ApproveRequestModal: React.FC<{
  loading: boolean;
  request: FirestoreDoc<PermissionRequest> | undefined;
  onClose: () => void;
}> = ({ loading, request, onClose }) => {
  const optionsDoc = useDurationSettings();
  const [approvalLength, setApprovalLength] = useState<string>();
  const [approvalLoading, setApprovalLoading] = useState(false);
  const authFetch = useAuthFetch(undefined, setApprovalLoading);

  const baseOptions = useMemo(
    () =>
      [
        ...(optionsDoc.doc?.data.options ?? DEFAULT_DURATION_SETTINGS.options),
      ].sort(sortApprovalOptions),
    [optionsDoc]
  );

  const options = useMemo(
    () =>
      uniqBy(
        compact([
          buildCustomRequestDurationOption(
            request?.data.duration,
            request?.data.originalRequestedDuration
          ),
          ...baseOptions,
        ]).map((option) => ({
          value: `${option.time}${option.unit}`,
          label: option.value,
        })),
        (option) => option.value
      ),
    [request, baseOptions]
  );

  const updateRequestDuration = useCallback(
    (value: string) => setApprovalLength(value),
    []
  );

  const handleApprove = useCallback(
    async (requestId: string) => {
      const response = await authFetch(
        `permission-requests/${requestId}/approve`,
        {
          method: "POST",
          json: {
            expirationLength: approvalLength || options[0].value,
          },
        }
      );
      handleRequestActionResponse(response, "Request approved");
      if (response?.ok) {
        onClose();
      }
    },
    [approvalLength, options, authFetch, onClose]
  );

  const closeModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const approveRequest = useCallback(
    () => request && handleApprove(request.id),
    [handleApprove, request]
  );

  const attribution = request?.data
    ? toRequestAttribution(request.data)
    : undefined;

  return (
    <Modal
      title="Approve request"
      open={request !== undefined}
      onCancel={closeModal}
      footer={
        request && [
          <Button
            key="submit"
            type="primary"
            onClick={approveRequest}
            loading={loading || approvalLoading}
          >
            Approve
          </Button>,
        ]
      }
    >
      {request?.data && attribution ? (
        <>
          <Typography.Paragraph>
            For how long would you like to grant{" "}
            {/* Approvers must read the whole grant; long values such as
                ARNs have no spaces, so let them wrap */}
            <Typography.Text underline className="break-words">
              {requestDescription(request.data, { truncate: false })}
            </Typography.Text>{" "}
            to{" "}
            <Identity
              value={{
                type: attribution.requestor.type,
                sub: attribution.onBehalfOf ?? attribution.requestor.sub,
                role: "principal",
              }}
            />
            ?
          </Typography.Paragraph>
          <Select
            className="w-full"
            options={options}
            value={approvalLength || options[0].value}
            onChange={updateRequestDuration}
          />
        </>
      ) : (
        <Typography.Paragraph>No request selected</Typography.Paragraph>
      )}
    </Modal>
  );
};
